import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, Zap, Star, ChevronDown, Globe, Sparkles, ShieldCheck, Lock } from 'lucide-react';
import { cn } from '../lib/utils';
import { Link } from 'react-router-dom';
import { Switch } from '../components/ui/Switch';
import { toast } from '../lib/feedbackToast';

// Comprehensive PPP mapping for global reach
const PPP_RATES = {
  // North America
  US: { currency: '$', multiplier: 1 },
  CA: { currency: 'C$', multiplier: 1.35 },
  MX: { currency: 'MX$', multiplier: 17 }, // Discounted
  
  // Europe
  GB: { currency: '£', multiplier: 0.8 },
  EU: { currency: '€', multiplier: 0.92 },
  CH: { currency: 'CHF', multiplier: 0.9 },
  SE: { currency: 'kr', multiplier: 10.5 },
  NO: { currency: 'kr', multiplier: 10.8 },
  
  // Asia-Pacific
  AU: { currency: 'A$', multiplier: 1.5 },
  NZ: { currency: 'NZ$', multiplier: 1.6 },
  JP: { currency: '¥', multiplier: 150 },
  IN: { currency: '₹', multiplier: 45 }, // Heavily Discounted for PPP
  CN: { currency: '¥', multiplier: 7.2 },
  KR: { currency: '₩', multiplier: 1300 },
  SG: { currency: 'S$', multiplier: 1.35 },
  ID: { currency: 'Rp', multiplier: 10000 }, // Discounted
  PH: { currency: '₱', multiplier: 40 }, // Discounted
  
  // Latin America
  BR: { currency: 'R$', multiplier: 3.5 }, // Discounted
  AR: { currency: '$', multiplier: 800 }, // Heavily Discounted
  CO: { currency: '$', multiplier: 3000 }, // Discounted
  CL: { currency: '$', multiplier: 750 }, // Discounted
  
  // Africa & Middle East
  ZA: { currency: 'R', multiplier: 12 }, // Discounted
  AE: { currency: 'AED', multiplier: 3.67 },
  SA: { currency: 'SAR', multiplier: 3.75 },
  EG: { currency: 'E£', multiplier: 30 }, // Discounted
  NG: { currency: '₦', multiplier: 800 }, // Discounted

  DEFAULT: { currency: '$', multiplier: 1 }
};

const BASE_PRICES = {
  bloom: 9,
  canopy: 24
};

const FAQS = [
  {
    question: "Why is Linkit currently invite-only?",
    answer: "We are meticulously rolling out access to ensure our infrastructure scales perfectly. This helps us maintain an ultra-premium, stable ecosystem for our early adopters."
  },
  {
    question: "How long is the waitlist?",
    answer: "The wait time varies based on your region and creator profile. We release new invite codes in weekly cohorts. Join the waitlist to secure your spot in line."
  },
  {
    question: "Can I reserve my @handle while waiting?",
    answer: "Yes! By joining the waitlist, you flag your interest. When you receive your invite code, you will have priority access to claim your desired handle."
  },
  {
    question: "Will I have access to the free tier?",
    answer: "Yes, once admitted, you can choose the Seed tier which is absolutely free forever, or instantly upgrade to Bloom or Canopy for premium features."
  }
];

export default function Pricing() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isYearly, setIsYearly] = useState(false);
  const [geoData, setGeoData] = useState({ country: 'US', loading: true });
  const [openFaq, setOpenFaq] = useState(null);
  const waitlistRef = useRef(null);

  useEffect(() => {
    // ── Linktree-Inspired HEAD Request Geolocation ──
    const fetchGeo = async () => {
      try {
        // Local simulation override
        const params = new URLSearchParams(window.location.search);
        if (params.has('sim_country')) {
          setGeoData({ country: params.get('sim_country').toUpperCase(), loading: false });
          return;
        }

        // Cache check
        const cached = sessionStorage.getItem('linkit_country');
        if (cached) {
          setGeoData({ country: cached, loading: false });
          return;
        }

        // Extremely fast HEAD request to current page
        const res = await fetch(window.location.href, { method: "HEAD" });
        // Vercel standard headers
        let code = res.headers.get("x-vercel-ip-country") || res.headers.get("Country-Code") || "US";
        
        // Ensure EU countries map to EU pricing if not strictly mapped, or fallback to US
        if (!PPP_RATES[code]) {
          // Broad check for unmapped European countries
          const euSet = new Set(['AT','BE','CY','EE','FI','FR','DE','GR','IE','IT','LV','LT','LU','MT','NL','PT','SK','SI','ES']);
          code = euSet.has(code) ? 'EU' : 'DEFAULT';
        }

        sessionStorage.setItem('linkit_country', code);
        setGeoData({ country: code, loading: false });
      } catch (err) {
        setGeoData({ country: 'US', loading: false });
      }
    };
    fetchGeo();
  }, []);

  const rate = PPP_RATES[geoData.country] || PPP_RATES['DEFAULT'];
  // We consider regions with a significantly higher multiplier than standard FX as "Discounted"
  const isDiscountedRegion = ['IN', 'BR', 'MX', 'ZA', 'AR', 'CO', 'CL', 'EG', 'NG', 'ID', 'PH'].includes(geoData.country);
  
  const getPrice = (basePrice) => {
    const monthlyPrice = Math.round(basePrice * rate.multiplier);
    if (isYearly) {
      // 2 months free equivalent (10 months instead of 12)
      return Math.round((monthlyPrice * 10) / 12);
    }
    return monthlyPrice;
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) return;
    
    setSubmitting(true);
    try {
      const res = await fetch('/api/mail?action=waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'pricing' })
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.alreadyRegistered) {
          toast.error("You already have an account.", { description: "Please log in instead." });
        } else if (data.alreadyJoined) {
          toast.success("You are already on the list!", { description: "We'll email you when a spot opens." });
        } else {
          setSubmitted(true);
          setTimeout(() => setSubmitted(false), 5000);
          setEmail('');
        }
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || "Something went wrong. Please try again.");
      }
    } catch (err) {
      alert("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToWaitlist = () => {
    waitlistRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="min-h-screen text-[#0F172A] pt-24 pb-32 px-4 sm:px-8 relative overflow-hidden bg-[#F8FAFC] selection:bg-[#BE185D]/20 selection:text-[#0F172A]">
      
      {/* Premium Nature Background Glows (Stone, Berry, Ocean) */}
      <div className="absolute top-[-10%] left-[-10%] w-[60vw] h-[60vw] bg-[#94A3B8] opacity-[0.2] blur-[120px] rounded-full pointer-events-none mix-blend-multiply" />
      <div className="absolute top-[20%] right-[-15%] w-[70vw] h-[70vw] bg-[#BE185D] opacity-[0.1] blur-[150px] rounded-full pointer-events-none mix-blend-multiply" />
      <div className="absolute bottom-[-20%] left-[10%] w-[80vw] h-[60vw] bg-[#1E3A8A] opacity-[0.1] blur-[140px] rounded-full pointer-events-none mix-blend-multiply" />
      <div className="absolute bottom-[10%] right-[20%] w-[40vw] h-[40vw] bg-[#64748B] opacity-[0.15] blur-[90px] rounded-full pointer-events-none mix-blend-multiply" />

      <div className="max-w-6xl mx-auto relative z-10 flex flex-col items-center mt-12 md:mt-16">
        
        {/* Badge */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="glass-pill inline-flex items-center justify-center px-4 py-1.5 mb-8 border border-[#0F172A]/10 bg-white/40"
        >
          <span className="text-xs font-bold tracking-widest uppercase text-[#0F172A]">Invite Only</span>
        </motion.div>

        {/* Hero Copy */}
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
          className="text-5xl sm:text-6xl md:text-7xl font-medium tracking-tight mb-6 text-center text-[#0F172A]"
          style={{ fontFamily: "'Playfair Display', serif", lineHeight: 1.1 }}
        >
          Claim your space.
        </motion.h1>

        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          className="text-lg sm:text-xl opacity-75 max-w-2xl mx-auto mb-10 text-center font-light"
        >
          Linkit is meticulously rolling out to early adopters. Preview our pricing below and plant your roots on the waitlist.
        </motion.p>

        {/* Billing Toggle & Geo Indicator (Hidden during Invite Only phase) */}
        {/*
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
          className="flex flex-col items-center mb-16"
        >
          <div className="flex items-center gap-4 mb-4">
            <span className={cn("text-sm font-bold transition-opacity", !isYearly ? "text-[#0F172A]" : "text-[#0F172A]/50")}>Monthly</span>
            <Switch checked={isYearly} onCheckedChange={setIsYearly} />
            <span className={cn("text-sm font-bold transition-opacity flex items-center gap-2", isYearly ? "text-[#0F172A]" : "text-[#0F172A]/50")}>
              Yearly <span className="text-[10px] uppercase tracking-wider bg-[#1E3A8A]/10 text-[#1E3A8A] px-2 py-0.5 rounded-full font-black">2 months free</span>
            </span>
          </div>
          
          <AnimatePresence>
            {!geoData.loading && geoData.country !== 'US' && geoData.country !== 'DEFAULT' && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="flex items-center gap-2 text-xs font-medium opacity-90 bg-white/60 border border-[#0F172A]/10 px-4 py-1.5 rounded-full shadow-sm"
              >
                <Globe size={14} className="text-[#1E3A8A]" />
                Pricing localized for {geoData.country}
                {isDiscountedRegion && <span className="text-[#BE185D] font-bold"> • PPP Discount Applied</span>}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        */}

        {/* Pricing Cards with Coming Soon Overlay */}
        <div className="relative w-full mb-24">
          
          {/* Frosted Glass Overlay */}
          <div className="absolute inset-[-20px] z-20 flex flex-col items-center justify-center bg-[#F8FAFC]/30 backdrop-blur-[8px] rounded-[3rem] border border-white/50 shadow-[0_30px_60px_rgba(15,23,42,0.1)]">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col items-center"
            >
              <div className="bg-[#0F172A] text-white px-8 py-4 rounded-full font-bold text-2xl md:text-3xl tracking-tight shadow-xl mb-4 flex items-center gap-3">
                <Lock size={24} className="text-[#BE185D]" /> Unlocking Soon
              </div>
              <p className="text-[#0F172A] font-medium opacity-80 max-w-md text-center px-4">
                We are currently finalizing our global infrastructure. Join the waitlist below to secure your early-adopter spot.
              </p>
              <button onClick={scrollToWaitlist} className="mt-6 font-bold text-[#BE185D] flex items-center gap-2 hover:scale-105 active:scale-95 transition-transform bg-white/80 px-6 py-2 rounded-full shadow-sm border border-[#BE185D]/20">
                Skip to Waitlist <ArrowRight size={16} />
              </button>
            </motion.div>
          </div>

          <div className="w-full grid lg:grid-cols-3 gap-6 lg:gap-8 items-stretch opacity-60 pointer-events-none select-none blur-[2px]">
          
          {/* Seed Tier (River Stone) */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="glass-surface-stone rounded-[2rem] p-8 flex flex-col"
          >
            <h3 className="text-2xl font-bold mb-2 text-[#475569]">Seed</h3>
            <p className="text-sm opacity-70 mb-6 font-medium">For planting your roots.</p>
            <div className="mb-8">
              <span className="text-4xl font-bold text-[#0F172A]">{rate.currency}0</span>
              <span className="opacity-60 text-sm font-medium">/mo</span>
            </div>
            <ul className="space-y-4 mb-8 flex-1">
              {['Unlimited links & embeds', 'Essential analytics', 'Standard themes', 'Unique QR code', '5% transaction fee'].map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-sm font-medium opacity-80 text-[#0F172A]">
                  <Check size={18} className="text-[#64748B] shrink-0 mt-0.5" />
                  {feature}
                </li>
              ))}
            </ul>
            <button className="w-full py-4 rounded-xl border border-[#0F172A]/20 text-[#0F172A] font-bold">
              Request Invite
            </button>
          </motion.div>

          {/* Bloom Tier (Wild Berry / Highlighted) */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="glass-surface-berry rounded-[2rem] p-8 flex flex-col relative transform md:-translate-y-4"
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#BE185D] text-white text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full flex items-center gap-1 shadow-[0_4px_14px_rgba(190,24,93,0.3)]">
              <Sparkles size={12} fill="currentColor" /> Recommended
            </div>
            <h3 className="text-2xl font-bold mb-2 text-[#9D174D]">Bloom</h3>
            <p className="text-sm opacity-80 mb-6 font-medium text-[#831843]">For creators ready to thrive.</p>
            <div className="mb-2 flex items-end gap-1">
              <span className="text-5xl font-bold text-[#831843]">{rate.currency}{getPrice(BASE_PRICES.bloom)}</span>
              <span className="opacity-70 text-sm mb-1 font-medium text-[#831843]">/mo</span>
            </div>
            <div className="text-xs opacity-70 mb-8 font-bold h-4 text-[#831843]">
              {isYearly ? `Billed ${rate.currency}${getPrice(BASE_PRICES.bloom) * 12} yearly` : 'Billed monthly'}
            </div>
            <ul className="space-y-4 mb-8 flex-1">
              {['Everything in Seed', '0% transaction fees', 'Custom domain support', 'Deep audience analytics', 'Automated Instagram replies', 'Vibrant custom palettes'].map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-sm font-bold text-[#4C0519]">
                  <Check size={18} className="text-[#BE185D] shrink-0 mt-0.5" />
                  {feature}
                </li>
              ))}
            </ul>
            <button className="w-full py-4 rounded-xl bg-gradient-to-r from-[#9D174D] to-[#BE185D] text-white font-bold flex justify-center items-center gap-2">
              Join Bloom Waitlist <ArrowRight size={16} />
            </button>
          </motion.div>

          {/* Canopy Tier (Deep Ocean) */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="glass-surface-ocean rounded-[2rem] p-8 flex flex-col"
          >
            <h3 className="text-2xl font-bold mb-2 text-[#1E3A8A]">Canopy</h3>
            <p className="text-sm opacity-80 mb-6 font-medium text-[#1E3A8A]">For brands commanding the ecosystem.</p>
            <div className="mb-2 flex items-end gap-1">
              <span className="text-4xl font-bold text-[#0F172A]">{rate.currency}{getPrice(BASE_PRICES.canopy)}</span>
              <span className="opacity-70 text-sm mb-1 font-medium text-[#1E3A8A]">/mo</span>
            </div>
             <div className="text-xs opacity-70 mb-8 font-bold h-4 text-[#1E3A8A]">
              {isYearly ? `Billed ${rate.currency}${getPrice(BASE_PRICES.canopy) * 12} yearly` : 'Billed monthly'}
            </div>
            <ul className="space-y-4 mb-8 flex-1">
              {['Everything in Bloom', 'Concierge onboarding', 'Unlimited social posts', 'API access & webhooks', 'Team collaboration tools', '100% affiliate commission'].map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-sm font-bold text-[#0F172A]">
                  <ShieldCheck size={18} className="text-[#1E3A8A] shrink-0 mt-0.5" />
                  {feature}
                </li>
              ))}
            </ul>
            <button className="w-full py-4 rounded-xl border-2 border-[#1E3A8A]/30 text-[#1E3A8A] font-bold">
              Request Invite
            </button>
          </motion.div>

          </div>
        </div>

        {/* Waitlist Form Section */}
        <motion.div 
          ref={waitlistRef}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="w-full bg-white/40 backdrop-blur-xl rounded-[2.5rem] p-8 sm:p-12 shadow-[0_20px_60px_rgba(15,23,42,0.05)] border border-white/60 flex flex-col md:flex-row gap-12 items-center mb-24 relative overflow-hidden"
        >
          {/* Subtle inner nature glow */}
          <div className="absolute top-0 right-0 w-[50%] h-[100%] bg-gradient-to-l from-[#94A3B8]/10 to-transparent pointer-events-none" />

          <div className="flex-1 space-y-4 w-full relative z-10">
            <h3 className="text-4xl font-bold text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>Secure your spot.</h3>
            <p className="text-base opacity-75 max-w-md font-medium text-[#0F172A]">
              We are deliberately limiting access to ensure ecosystem stability. Join the waitlist to reserve your desired @handle.
            </p>
          </div>

          <div className="flex-1 w-full bg-white/60 rounded-[2rem] p-6 sm:p-8 border border-white/80 shadow-inner relative z-10">
            <h4 className="text-xl font-bold mb-3 text-[#0F172A]">Join the Waitlist</h4>
            <p className="text-sm opacity-70 mb-8 font-medium text-[#0F172A]">
              We'll notify you as soon as an invite code becomes available.
            </p>
            
            <form onSubmit={handleJoin} className="flex flex-col gap-4">
              <input 
                type="email" 
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting || submitted}
                className="w-full bg-white/80 rounded-xl py-4 px-6 text-[#0F172A] placeholder:text-[#0F172A]/40 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 transition-all border border-white text-center sm:text-left disabled:opacity-50 font-medium shadow-sm"
                required
              />
              <button 
                type="submit"
                disabled={submitting || submitted}
                className={cn(
                  "w-full py-4 rounded-xl font-bold text-base transition-all flex items-center justify-center gap-2 disabled:cursor-not-allowed shadow-md",
                  submitted ? "bg-[#10B981] text-white" : "bg-[#0F172A] text-white hover:bg-[#1E293B] hover:scale-[1.02] active:scale-[0.98]"
                )}
              >
                {submitting ? "Joining..." : submitted ? "You're on the list!" : "Secure My Spot"}
                {!submitted && !submitting && <ArrowRight size={18} />}
              </button>
            </form>
          </div>
        </motion.div>

        {/* FAQs */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="w-full max-w-3xl"
        >
          <h2 className="text-4xl font-bold text-center mb-10 text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>Frequently Asked Questions</h2>
          <div className="space-y-4">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-white/40 backdrop-blur-md rounded-2xl overflow-hidden border border-white/60 shadow-sm">
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left font-bold text-[#0F172A] hover:bg-white/20 transition-colors"
                >
                  {faq.question}
                  <ChevronDown size={18} className={cn("transition-transform text-[#1E3A8A]", openFaq === i ? "rotate-180" : "")} />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-6 pb-5 text-sm font-medium opacity-80 leading-relaxed text-[#0F172A]"
                    >
                      {faq.answer}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </motion.div>

      </div>

      {/* Floating Action Button (Go Home) */}
      <Link to="/">
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 px-8 py-3 bg-white/60 backdrop-blur-xl w-max flex items-center gap-3 hover:scale-105 active:scale-95 transition-all z-50 rounded-full shadow-[0_8px_20px_rgba(15,23,42,0.1)] border border-white/80">
           <span className="font-black text-[10px] uppercase tracking-widest text-[#0F172A] whitespace-nowrap">Back to Linkit</span>
        </div>
      </Link>
    </div>
  );
}
